import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { LogoIcon, contact1 } from '../assets';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, SplitText);

const Slider = () => {
  const sliderRef = useRef(null);
  const progressBarRef = useRef(null);
  const sliderImagesRef = useRef(null);
  const sliderTitleRef = useRef(null);
  const sliderSubtitleRef = useRef(null);
  const sliderIndicesRef = useRef(null);
  const activeSlideRef = useRef(0);
  const currentSplitTitleRef = useRef(null);
  const currentSplitSubtitleRef = useRef(null);
  const lenisRef = useRef(null);
  const scrollTriggerRef = useRef(null);

  const slides = [
    {
      title: "Backing the Builders of Africa's Creative and Digital Future",
      subtitle: "Catalyzing Africa's innovation economy by backing visionary founders",
      image: contact1,
    },
    {
      title: "Investing in scalable, tech enabled African ventures solving real-world problems",
      subtitle: "AfriNext Ventures exists to be the most founder-centric venture partner powering Africa's next unicorns",
      image: contact1,
    },
  ];

  const createIndices = () => {
    if (!sliderIndicesRef.current) return;
    
    sliderIndicesRef.current.innerHTML = "";
    
    slides.forEach((_, index) => {
      const indexNum = (index + 1).toString().padStart(2, "0");
      const indicatorElement = document.createElement("p");
      indicatorElement.dataset.index = index;
      indicatorElement.className = "flex items-center gap-2 cursor-pointer m-0 font-mono text-sm text-third hover:opacity-100 transition-opacity duration-300";
      indicatorElement.innerHTML = `
        <span class="marker w-5 h-0.5 bg-black origin-left inline-block"></span>
        <span class="index">${indexNum}</span>
      `;
      sliderIndicesRef.current.appendChild(indicatorElement);
    });
    
    // Set initial states for all indicators
    const indicators = sliderIndicesRef.current.querySelectorAll("p");
    indicators.forEach((indicator, i) => {
      const markerEl = indicator.querySelector(".marker");
      const indexEl = indicator.querySelector(".index");
      if (markerEl && indexEl) {
        if (i === 0) {
          gsap.set(markerEl, { scaleX: 1 });
          gsap.set(indexEl, { opacity: 1 });
        } else {
          gsap.set(markerEl, { scaleX: 0 });
          gsap.set(indexEl, { opacity: 0.35 });
        }
      }
    });
  };

  const animateNewTitle = (index) => {
    if (!sliderTitleRef.current) return;
    
    if (currentSplitTitleRef.current) {
      currentSplitTitleRef.current.revert();
    }

    // Clear and add new title
    sliderTitleRef.current.innerHTML = '';
    const titleElement = document.createElement('h1');
    // Fixed typography classes - properly sized and visible
    titleElement.className = "text-white kerning-normal text-md xs:text-lg font-bold text-center max-w-4xl mx-auto px-6 leading-tight sm:leading-tight md:leading-tight";
    titleElement.textContent = slides[index].title;
    sliderTitleRef.current.appendChild(titleElement);

    currentSplitTitleRef.current = new SplitText(titleElement, {
      type: "lines",
      linesClass: "line overflow-hidden block",
    });

    gsap.set(currentSplitTitleRef.current.lines, {
      yPercent: 100,
      opacity: 0,
    });

    gsap.to(currentSplitTitleRef.current.lines, {
      yPercent: 0,
      opacity: 1,
      duration: 0.75,
      stagger: 0.1,
      ease: "power2.out",
    });
  };

  const animateNewSubtitle = (index) => {
    if (!sliderSubtitleRef.current) return;
    
    if (currentSplitSubtitleRef.current) {
      currentSplitSubtitleRef.current.revert();
    }

    // Clear and add new subtitle
    sliderSubtitleRef.current.innerHTML = '';
    const subtitleElement = document.createElement('p');
    // Fixed typography classes - properly sized and visible
    subtitleElement.className = "text-white xs:text-md text-mm text-center max-w-3xl leading-tight mx-auto px-4 mt-4 sm:mt-6 ";
    subtitleElement.textContent = slides[index].subtitle;
    sliderSubtitleRef.current.appendChild(subtitleElement);

    currentSplitSubtitleRef.current = new SplitText(subtitleElement, {
      type: "lines",
      linesClass: "line overflow-hidden block",
    });

    gsap.set(currentSplitSubtitleRef.current.lines, {
      yPercent: 100,
      opacity: 0,
    });

    gsap.to(currentSplitSubtitleRef.current.lines, {
      yPercent: 0,
      opacity: 1,
      duration: 0.75,
      stagger: 0.1,
      delay: 0.2,
      ease: "power2.out",
    });
  };

  const animateNewIndicators = (index) => {
    if (!sliderIndicesRef.current) return;
    
    const indicators = sliderIndicesRef.current.querySelectorAll("p");

    indicators.forEach((indicator, i) => {
      const markerElement = indicator.querySelector(".marker");
      const indexElement = indicator.querySelector(".index");
      
      if (!markerElement || !indexElement) return;

      if (i === index) {
        gsap.to(indexElement, {
          opacity: 1,
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(markerElement, {
          scaleX: 1,
          duration: 0.3,
          ease: "power2.out",
        });
      } else {
        gsap.to(indexElement, {
          opacity: 0.35,
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(markerElement, {
          scaleX: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    });
  };

  const animateNewSlide = (index) => {
    if (!sliderImagesRef.current) return;
    
    const newSliderImage = document.createElement("img");
    newSliderImage.src = slides[index].image;
    newSliderImage.alt = `${index + 1}`;
    // Fixed image classes - ensures w-full and h-screen
    newSliderImage.className = "absolute top-0 left-0 w-full h-screen object-cover";
    newSliderImage.style.width = "100%";
    newSliderImage.style.height = "100vh";
    newSliderImage.style.objectFit = "cover";
    newSliderImage.loading = "lazy";

    gsap.set(newSliderImage, {
      opacity: 0,
      scale: 1,
    });

    // Clear existing images and add new one
    while (sliderImagesRef.current.firstChild) {
      sliderImagesRef.current.removeChild(sliderImagesRef.current.firstChild);
    }
    
    sliderImagesRef.current.appendChild(newSliderImage);

    gsap.to(newSliderImage, {
      opacity: 1,
      duration: 0.8,
      ease: "power2.inOut",
    });

    animateNewTitle(index);
    animateNewSubtitle(index);
    animateNewIndicators(index);
  };

  useEffect(() => {
    if (!sliderRef.current) return;

    // Initialize Lenis for smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    lenis.on("scroll", ScrollTrigger.update);
    
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    const pinDistance = window.innerHeight * slides.length;
    
    createIndices();
    
    // Initial slide setup
    setTimeout(() => {
      animateNewSlide(0);
    }, 100);

    // Create ScrollTrigger
    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: sliderRef.current,
      start: "top top",
      end: `+=${pinDistance}px`,
      scrub: 1,
      pin: true,
      pinSpacing: true,
      onUpdate: (self) => {
        if (progressBarRef.current) {
          gsap.set(progressBarRef.current, {
            scaleY: self.progress,
          });
        }

        const currentSlide = Math.floor(self.progress * slides.length);
        
        if (activeSlideRef.current !== currentSlide && currentSlide < slides.length) {
          activeSlideRef.current = currentSlide;
          animateNewSlide(activeSlideRef.current);
        }
      },
    });

    // Cleanup
    return () => {
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
      }
      if (lenisRef.current) {
        lenisRef.current.destroy();
      }
      if (currentSplitTitleRef.current) {
        currentSplitTitleRef.current.revert();
      }
      if (currentSplitSubtitleRef.current) {
        currentSplitSubtitleRef.current.revert();
      }
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      gsap.killTweensOf(progressBarRef.current);
    };
  }, []);

  return (
    <div
      className="relative w-full min-h-screen overflow-hidden bg-white" ref={sliderRef}
    >
      {/* Progress Bar */}
      <div 
        className="fixed right-8 top-0 w-1 h-screen bg-gradient-to-t from-tertiary to-gray-500 origin-top z-[100] scale-y-0 rounded-full"
        ref={progressBarRef}
      ></div>
      
      {/* Images Container - Fixed for full screen */}
      <div className="relative w-full h-screen overflow-hidden bg-cover bg-no-repeat bg-bottom" ref={sliderImagesRef} alt="pexels-mukula-igavinchi-443985808-15496531.webp">
        {/* Images will be added dynamically */}
      </div>
      
      {/* Title and Subtitle Container - Fixed positioning and visibility */}
      <div className="absolute bottom-60 left-0 right-0 z-20 flex flex-col items-center justify-center pb-16 sm:pb-20 md:pb-24 px-4 pointer-events-none">
        <div className="w-full max-w-7xl mx-auto flex flex-col items-center gap-8">
          <img
            src={LogoIcon}
            alt="AfriNext"
            className="w-[70px] h-[70px] object-contain mb-10"
          />
          {/* Title */}
          <div ref={sliderTitleRef} className="w-full mb-4"></div>
          
          {/* Subtitle */}
          <div ref={sliderSubtitleRef} className="w-full"></div>
        </div>
      </div>
      
      {/* Indices */}
      <div className="fixed right-4 sm:right-6 md:right-8 bottom-4 sm:bottom-6 md:bottom-8 flex flex-col gap-3 sm:gap-4 z-[100]" ref={sliderIndicesRef}>
        {/* Indices will be added dynamically */}
      </div>
      
      {/* Top gradient overlay */}
      <div className="absolute inset-0 origin-top pointer-events-none z-[5]"></div>
    </div>
  );
};

export default Slider;